import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Camera, Video, Mic, Image as ImageIcon, Globe, ArrowRight, Calendar, Clock, Sunrise, Palette, CheckCircle, FileText, Search } from 'lucide-react';
import { services } from '../data/services';

const iconMap = { Camera, Video, Mic, Image: ImageIcon, Globe, Calendar, Clock, Sunrise, Palette, CheckCircle, FileText, Search };

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function SidebarContactForm() {
  const [sf, setSf] = useState({ name: '', email: '', phone: '', message: '' });
  const [sfStatus, setSfStatus] = useState('');
  const handleSf = async (e) => {
    e.preventDefault();
    if (!sf.name || !sf.email) return;
    setSfStatus('sending');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '9f4d14d4-78e5-423d-97cb-960719d5d9f2',
          from_name: 'Image & Rivière - Prestations',
          subject: '[Prestation] Demande rapide',
          ...sf,
        }),
      });
      const data = await res.json();
      setSfStatus(data.success ? 'sent' : 'error');
    } catch { setSfStatus('error'); }
  };

  if (sfStatus === 'sent') {
    return (
      <div className="glass-light rounded-2xl p-6 text-center">
        <p className="font-montserrat text-sm text-[#7DD4F5]">Message envoyé !</p>
      </div>
    );
  }

  return (
    <div className="glass-light rounded-2xl p-6">
      <h3 className="font-montserrat text-sm font-semibold text-white mb-4">Vous avez un projet ?</h3>
      <form className="flex flex-col gap-3" onSubmit={handleSf}>
        <input className="form-input text-sm" placeholder="Nom *" value={sf.name} onChange={e => setSf({...sf, name: e.target.value})} data-testid="sidebar-form-name" />
        <input className="form-input text-sm" placeholder="Email *" value={sf.email} onChange={e => setSf({...sf, email: e.target.value})} data-testid="sidebar-form-email" />
        <input className="form-input text-sm" placeholder="Téléphone" value={sf.phone} onChange={e => setSf({...sf, phone: e.target.value})} data-testid="sidebar-form-phone" />
        <textarea className="form-input text-sm" rows={3} placeholder="Message" value={sf.message} onChange={e => setSf({...sf, message: e.target.value})} data-testid="sidebar-form-message" />
        <button data-testid="sidebar-form-submit" disabled={sfStatus === 'sending'} className="w-full py-2.5 rounded-xl bg-[#2B7BA8] text-white font-montserrat text-sm hover:bg-[#5AACE0] transition-colors disabled:opacity-50">
          {sfStatus === 'sending' ? 'Envoi...' : 'Envoyer ma demande'}
        </button>
        {sfStatus === 'error' && <p className="font-lato text-xs text-red-400">Erreur, veuillez réessayer.</p>}
      </form>
    </div>
  );
}

function ServicesOverview() {
  return (
    <main data-testid="services-page">
      <section className="relative h-[40vh] overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/prq4obov__DSC4113.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D15] via-[#0B1D15]/60 to-[#0B1D15]/30" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 z-10 px-6">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-5xl md:text-6xl text-white text-center tracking-tight mb-3">
            Mes Prestations
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="font-lato text-lg text-[#8CA1B0] text-center">
            Au service des milieux aquatiques et de ceux qui les defendent
          </motion.p>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-[#0B1D15] to-[#0F2318]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col gap-20">
            {services.map((s, i) => {
              const isReversed = i % 2 !== 0;
              return (
                <FadeIn key={s.id} delay={0.1}>
                  <div data-testid={`prestation-card-${s.id}`} className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 items-center`}>
                    {/* Text side */}
                    <div className="lg:w-1/2">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-4 h-4 rounded-sm bg-[#2B7BA8] flex-shrink-0" />
                        <h3 className="font-montserrat text-2xl md:text-3xl font-bold text-white">{s.title}</h3>
                      </div>
                      <p className="font-montserrat text-sm font-medium text-[#C4A35A] mb-5 ml-7">{s.subtitle}</p>
                      <p className="font-lato text-base text-[#8CA1B0] leading-relaxed mb-6 ml-7">{s.fullDesc || s.shortDesc}</p>
                      <div className="ml-7">
                        <Link
                          to={`/prestations/${s.id}`}
                          data-testid={`prestation-btn-${s.id}`}
                          className="inline-block px-6 py-2.5 rounded-lg bg-[#2B7BA8] text-white font-montserrat text-sm hover:bg-[#5AACE0] transition-all duration-300"
                        >
                          Je decouvre !
                        </Link>
                      </div>
                    </div>
                    {/* Image side */}
                    <div className="lg:w-1/2">
                      <Link to={`/prestations/${s.id}`} className="block rounded-2xl overflow-hidden group">
                        <img
                          src={s.image}
                          alt={s.title}
                          className="w-full h-[280px] md:h-[340px] object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </Link>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

function ServiceDetail() {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  if (!service) return <div className="pt-32 text-center text-white">Service introuvable</div>;

  const Icon = iconMap[service.icon];

  return (
    <main data-testid={`service-detail-${serviceId}`} className="pt-24 bg-gradient-to-b from-[#0B1D15] to-[#0F2318] min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sidebar */}
          <aside className="lg:w-[30%] lg:sticky lg:top-28 lg:self-start">
            <div className="glass-light rounded-2xl p-6 mb-6">
              <h3 className="font-montserrat text-xs uppercase tracking-widest text-[#7DD4F5] mb-5">Mes prestations</h3>
              <div className="flex flex-col gap-2">
                {services.map(s => {
                  const SIcon = iconMap[s.icon];
                  return (
                    <Link
                      key={s.id}
                      to={`/prestations/${s.id}`}
                      data-testid={`sidebar-link-${s.id}`}
                      className={`font-lato text-sm py-2.5 px-4 rounded-xl transition-all flex items-center gap-2.5 ${
                        s.id === serviceId
                          ? 'bg-[#2B7BA8]/20 text-[#7DD4F5] border border-[#2B7BA8]/30'
                          : 'text-[#8CA1B0] hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <SIcon size={15} strokeWidth={1.5} className={s.id === serviceId ? 'text-[#7DD4F5]' : 'text-[#2B7BA8]'} />
                      {s.title}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Quick contact form */}
            <SidebarContactForm />
          </aside>

          {/* Content */}
          <div className="lg:w-[70%]">
            <FadeIn>
              <div className="rounded-2xl overflow-hidden mb-8">
                <img src={service.image} alt={service.title} className="w-full h-[300px] lg:h-[400px] object-cover" />
              </div>
            </FadeIn>

            <FadeIn delay={0.1}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#2B7BA8]/15 flex items-center justify-center">
                  <Icon size={24} className="text-[#7DD4F5]" />
                </div>
                <div>
                  <h1 className="font-playfair text-3xl md:text-4xl text-white">{service.title}</h1>
                  <p className="font-lato text-[#8CA1B0] mt-1">{service.subtitle}</p>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <p className="font-lato text-lg text-[#8CA1B0] leading-relaxed mb-8">{service.fullDesc}</p>
            </FadeIn>

            {/* Details (Bon a savoir) */}
            {service.details.length > 0 && (
              <FadeIn delay={0.3}>
                <div className="info-card rounded-2xl p-6 mb-8">
                  <h3 className="font-montserrat text-sm font-semibold text-[#0D2B3E] mb-4 uppercase tracking-wider">Bon a savoir</h3>
                  <div className="flex flex-col gap-3">
                    {service.details.map((d, i) => {
                      const DIcon = iconMap[d.icon] || CheckCircle;
                      return (
                        <div key={i} className="flex items-start gap-3">
                          <DIcon size={16} className="text-[#2B7BA8] mt-0.5 flex-shrink-0" />
                          <span className="font-lato text-sm text-[#4A4A4A]">{d.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </FadeIn>
            )}

            {/* Exhibitions */}
            {service.exhibitions && (
              <FadeIn delay={0.3}>
                <div className="space-y-6 mb-8">
                  {service.exhibitions.map((ex, i) => (
                    <div key={i} className="exhibition-card">
                      <h3 className="font-playfair text-xl text-white mb-3">"{ex.title}"</h3>
                      <p className="font-lato text-sm text-[#8CA1B0] leading-relaxed mb-3">{ex.desc}</p>
                      <p className="font-montserrat text-xs text-[#7DD4F5] italic">{ex.specs}</p>
                    </div>
                  ))}
                </div>
              </FadeIn>
            )}

            {/* Categories */}
            {service.categories && (
              <FadeIn delay={0.3}>
                <div className="mb-8">
                  <h3 className="font-montserrat text-sm font-semibold text-white mb-4 uppercase tracking-wider">Categories disponibles</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.categories.map((cat, i) => (
                      <span key={i} className="category-badge">{cat}</span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            )}

            {/* CTA */}
            <FadeIn delay={0.4}>
              {service.id === 'audiovisuel' ? (
                <a
                  href="https://aquatique-productions.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-detail-cta"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:bg-[#5AACE0] transition-all duration-300"
                >
                  {service.cta} <ArrowRight size={14} />
                </a>
              ) : (
                <Link
                  to="/contact"
                  data-testid="service-detail-cta"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:bg-[#5AACE0] transition-all duration-300"
                >
                  {service.cta} <ArrowRight size={14} />
                </Link>
              )}
            </FadeIn>
          </div>
        </div>
      </div>
    </main>
  );
}

export default function ServicesPage() {
  const { serviceId } = useParams();
  return serviceId ? <ServiceDetail /> : <ServicesOverview />;
}
